var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { apiGetListArticlePagePopularArticlePage, apiGetListArticlePagePopularProfessional, } from '@/actions/ArticleActions/getList';
import { apiGetListCaseRanking } from '@/actions/CaseActions';
import { usePromiseDoingCallback } from '@/hooks/async/loading/usePromiseDoingCallback';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { SESSION_STORAGE_KEY_POPULAR_LIST_ARTICLE, SESSION_STORAGE_KEY_POPULAR_LIST_CASE, SESSION_STORAGE_KEY_POPULAR_LIST_PROFESSIONAL, } from '@/utils/constants/storage/sessionStorageKey';
import { stringifyUrl } from '@/utils/libs/query-string/stringify';
import { useSessionStorage } from '@/utils/libs/react-hookz/useSessionStorage';
import { consultantSearchPath, consultantSearchTopPath, } from '@/utils/router/links';
/**
 * 人気記事を取得
 * @param arg
 */
const usePopularListColumn = (arg) => {
    const { dispatch, unmountRef } = arg;
    const { set: changePopulars, value: populars } = useSessionStorage(SESSION_STORAGE_KEY_POPULAR_LIST_ARTICLE);
    const popularsRef = useRef();
    popularsRef.current = populars;
    const fetchPopularArticle = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (popularsRef.current) {
            // 既にデータがある場合、取得不要
            return;
        }
        const result = yield wrapDispatch(dispatch, apiGetListArticlePagePopularArticlePage({}, {}));
        if (!unmountRef.current) {
            changePopulars(result.payload.list);
        }
    }), [unmountRef, changePopulars, dispatch, popularsRef]);
    // ローディング制御
    const { callbackPromiseEvent: loadArticlePopularColumn, doing: isLoading } = usePromiseDoingCallback({
        promiseEvent: fetchPopularArticle,
        unmountRef,
    });
    return { isLoading, loadArticlePopularColumn, populars };
};
/**
 * 人気コンサルタントを取得
 * @param arg
 */
const usePopularListProfessional = (arg) => {
    const { dispatch, keyword, unmountRef } = arg;
    const { set: changePopularProfessionals, value: popularProfessionals } = useSessionStorage(SESSION_STORAGE_KEY_POPULAR_LIST_PROFESSIONAL);
    const popularProfessionalsRef = useRef();
    popularProfessionalsRef.current = popularProfessionals;
    const formatKeyword = typeof keyword === 'undefined' ? '' : keyword;
    const fetchPopularProfessional = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if ((_a = popularProfessionalsRef.current) === null || _a === void 0 ? void 0 : _a[formatKeyword]) {
            // 既にデータがある場合、取得不要
            return;
        }
        const result = yield wrapDispatch(dispatch, apiGetListArticlePagePopularProfessional({ query: { keyword: formatKeyword } }, {}));
        if (!unmountRef.current) {
            changePopularProfessionals((prev) => (Object.assign(Object.assign({}, prev), { [formatKeyword]: result.payload.list })));
        }
    }), [
        unmountRef,
        changePopularProfessionals,
        dispatch,
        formatKeyword,
        popularProfessionalsRef,
    ]);
    // ローディング制御
    const { callbackPromiseEvent: loadArticlePopularProfessional, doing: isLoading, } = usePromiseDoingCallback({
        promiseEvent: fetchPopularProfessional,
        unmountRef,
    });
    const hasMoreLink = useMemo(() => {
        if (formatKeyword) {
            return stringifyUrl({
                query: { keyword: formatKeyword, search: 'search' },
                url: consultantSearchPath(),
            });
        }
        return consultantSearchTopPath();
    }, [formatKeyword]);
    return {
        hasMoreLink,
        isLoading,
        loadArticlePopularProfessional,
        popularProfessionals: popularProfessionals === null || popularProfessionals === void 0 ? void 0 : popularProfessionals[formatKeyword],
    };
};
/**
 * 人気検討事例を取得
 * @param arg
 */
const usePopularListCase = (arg) => {
    const { dispatch, unmountRef } = arg;
    const { set: changepopularCases, value: popularCases } = useSessionStorage(SESSION_STORAGE_KEY_POPULAR_LIST_CASE);
    const popularCasesRef = useRef();
    popularCasesRef.current = popularCases;
    const fetchPopularCase = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (popularCasesRef.current) {
            // 既にデータがある場合、取得不要
            return;
        }
        const result = yield wrapDispatch(dispatch, apiGetListCaseRanking({}, {}));
        if (!unmountRef.current) {
            // コンポーネントで参照できるように整形
            const cases = result.payload.list.map((item) => {
                var _a, _b, _c, _d;
                return ({
                    id: item.id,
                    src: ((_b = (_a = item.thumbnail_picture) === null || _a === void 0 ? void 0 : _a.default) === null || _b === void 0 ? void 0 : _b.src) || '',
                    title: item.title,
                    webpSrc: ((_d = (_c = item.thumbnail_picture) === null || _c === void 0 ? void 0 : _c.webp) === null || _d === void 0 ? void 0 : _d.src) || '',
                });
            });
            changepopularCases(cases);
        }
    }), [unmountRef, changepopularCases, dispatch, popularCasesRef]);
    // ローディング制御
    const { callbackPromiseEvent: loadArticlePopularCase, doing: isLoading } = usePromiseDoingCallback({
        promiseEvent: fetchPopularCase,
        unmountRef,
    });
    return {
        isLoading,
        loadArticlePopularCase,
        popularCases,
    };
};
/**
 * 人気記事、人気コンサルタント、人気事例を取得するuse
 * @param arg
 */
export const usePopularList = (arg) => {
    const { dispatch, isGuestUser, isProfessionalUser, keyword, unmountRef } = arg;
    const { isLoading: isLoadingPopularColumn, loadArticlePopularColumn, populars, } = usePopularListColumn({ dispatch, unmountRef });
    const { hasMoreLink: popularConsultantListHasMoreLink, isLoading: isLoadingPopularProfessional, loadArticlePopularProfessional, popularProfessionals, } = usePopularListProfessional({ dispatch, keyword, unmountRef });
    const { isLoading: isLoadingPopularCase, loadArticlePopularCase, popularCases, } = usePopularListCase({ dispatch, unmountRef });
    // API読み込み
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            yield Promise.all([
                loadArticlePopularColumn(),
                loadArticlePopularCase(),
                !isGuestUser && !isProfessionalUser
                    ? loadArticlePopularProfessional()
                    : null,
            ].filter((v) => v));
        }))();
    }, [
        isGuestUser,
        isProfessionalUser,
        loadArticlePopularColumn,
        loadArticlePopularProfessional,
        loadArticlePopularCase,
    ]);
    return {
        isLoadingPopularCase,
        isLoadingPopularColumn,
        isLoadingPopularProfessional,
        popularCases,
        popularConsultantListHasMoreLink,
        popularProfessionals,
        populars,
    };
};
